import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { useUpdateAll } from './useUpdateAll';
import { getChainConnection, getBackwardChain, getForwardChain } from '../helpers/getChainConnection';
import { getUpdatePositions } from '../helpers/getCountDays';
import { queueFetch } from '../helpers/queueFetch';
import { charterSelector } from '../store/reducers/charterSlice/charterSlice';
import { removeManyConnections } from '../store/reducers/positionsConnections/actions/deleteManyConnections';
import { positionActions, positionSelector } from '../store/reducers/positionSlice/positionSlice';
import { stageSelector } from '../store/reducers/stageSlice/stageSlice';

export const useEditPosition = (position) => {
    const dispatch = useDispatch();
    const allCharters = useSelector(charterSelector.selectAll);
    const allStage = useSelector(stageSelector.selectAll);
    const positionsEntitiesAll = useSelector(positionSelector.selectEntities);

    const parentCharter = allCharters.find((charter) => charter.children.includes(position.guid));

    const parentStage = allStage.find((stage) => {
        const childGuid = parentCharter ? parentCharter.guid : position.guid;

        return stage.positions.includes(childGuid);
    });

    const { updateAll } = useUpdateAll(parentStage);

    const removePositionConnection = (position) => {
        const { connections } = position;

        const fromPositionsType0 = connections
            .filter((connection) => +connection.type === 0 && connection.from !== position.guid)
            .map((connection) => connection.from);
        const toPositionsType0 = connections
            .filter((connection) => +connection.type === 0 && connection.to !== position.guid)
            .map((connection) => connection.to);

        const fromPositionsType1 = connections
            .filter((connection) => +connection.type === 1 && connection.from !== position.guid)
            .map((connection) => connection.from);
        const toPositionsType1 = connections
            .filter((connection) => +connection.type === 1 && connection.to !== position.guid)
            .map((connection) => connection.to);

        const needUpdatePosition = [
            ...fromPositionsType0,
            ...toPositionsType0,
            ...fromPositionsType1,
            ...toPositionsType1,
        ];

        const updatedPosition = {};

        needUpdatePosition.forEach((guid) => {
            const currentPosition = positionsEntitiesAll[guid];
            const updatedConnections = currentPosition.connections.filter((connection) => {
                if (+connection.type === 0) {
                    if (
                        currentPosition.guid === connection.from &&
                        position.guid === connection.to
                    ) {
                        return false;
                    }
                    if (
                        currentPosition.guid === connection.to &&
                        position.guid === connection.from
                    ) {
                        return false;
                    }
                    return true;
                }

                if (+connection.type === 1) {
                    if (
                        currentPosition.guid === connection.to &&
                        position.guid === connection.from
                    ) {
                        return false;
                    }
                    if (
                        currentPosition.guid === connection.from &&
                        position.guid === connection.to
                    ) {
                        return false;
                    }
                    return true;
                }
            });

            updatedPosition[guid] = { ...currentPosition, connections: updatedConnections };
        });

        return updatedPosition;
    };

    const updatedFollowersTypeOne = (moveDays, connectionsTypeOne, updatedPositionsEntities) => {
        const result = { ...updatedPositionsEntities };

        if (moveDays > 0) {
            connectionsTypeOne.forEach((connection) => {
                const currentPosition = positionsEntitiesAll[connection.to];
                const planned = {
                    ...currentPosition.planned,
                    start_date: moment(currentPosition.planned.start_date)
                        .add(moveDays, 'days')
                        .format(),
                    end_date: moment(currentPosition.planned.end_date)
                        .add(moveDays, 'days')
                        .format(),
                };
                result[currentPosition.guid] = {
                    ...currentPosition,
                    planned,
                };
            });
        } else {
            connectionsTypeOne.forEach((connection) => {
                const currentPosition = positionsEntitiesAll[connection.to];
                const diffBeforePosition =
                    moment(position.planned.start_date).diff(
                        moment(currentPosition.planned.end_date),
                        'days'
                    ) - 1;

                if (diffBeforePosition - Math.abs(moveDays) < 0) {
                    const planned = {
                        ...currentPosition.planned,
                        start_date: moment(currentPosition.planned.start_date)
                            .add(diffBeforePosition - Math.abs(moveDays), 'days')
                            .format(),
                        end_date: moment(currentPosition.planned.end_date)
                            .add(diffBeforePosition - Math.abs(moveDays), 'days')
                            .format(),
                    };

                    result[currentPosition.guid] = {
                        ...currentPosition,
                        planned,
                    };
                }
            });
        }
        return result;
    };

    const updatePositionIfFollowerTypeOne = (
        moveDays,
        updatedEndDate,
        updatedPositionsEntities
    ) => {
        const result = { ...updatedPositionsEntities };

        const predecessorConnections = position.connections.filter(
            (connection) => connection.type === 1 && connection.to === position.guid
        );

        const firstPredecessorStartDate = predecessorConnections
            .map((connection) => positionsEntitiesAll[connection.from])
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            )[0].planned.start_date;

        if (moment(updatedEndDate).isAfter(moment(firstPredecessorStartDate).add(-1, 'days'))) {
            const end_date = moment(firstPredecessorStartDate).add(-1, 'days').format();

            const start_date = moment(end_date)
                .add(-Number(position.planned.days_count) + 1, 'days')
                .format();

            const planned = {
                ...position.planned,
                start_date,
                end_date,
            };

            result[position.guid] = { ...position, planned };

            return result;
        }
        const planned = {
            ...position.planned,
            start_date: moment(position.planned.start_date).add(moveDays, 'days').format(),
            end_date: moment(position.planned.end_date).add(moveDays, 'days').format(),
        };
        result[position.guid] = { ...position, planned };

        return result;
    };

    const updatePositionWithPrev = (prevPositions, updatedPlanned) => {
        const lastPrevPosition = prevPositions[prevPositions.length - 1];

        let result = { ...updatedPlanned };

        if (
            moment(updatedPlanned.start_date).isBefore(
                moment(lastPrevPosition.planned.end_date).add(1, 'days')
            )
        ) {
            result = {
                ...updatedPlanned,
                start_date: moment(lastPrevPosition.planned.end_date).add(1, 'days').format(),
                end_date: moment(lastPrevPosition.planned.end_date)
                    .add(updatedPlanned.days_count, 'days')
                    .format(),
            };
            return result;
        }
        return result;
    };

    const updateNextPosition = (
        nextPositions,
        updatedPosition,
        moveDays,
        updatedPositionsEntities
    ) => {
        const result = { ...updatedPositionsEntities };

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getChainConnection(position, positionsEntitiesAll),
        ]);
        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = position.planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    result[position.guid] = updatedPosition;

                    updatePosition.forEach((item) => {
                        result[item.id] = item.changes;
                    });
                }
            });
        }
        return result;
    };

    const resetPosition = () => {
        const resetPlanned = {
            days_count: null,
            start_date: null,
            end_date: null,
        };

        const connections = position.connections.map((connection) => ({
            positionFromGuid: connection.from,
            positionToGuid: connection.to,
            connectionType: connection.type,
        }));
        const removedConnections = position.connections.map((connection) => ({
            from: connection.from,
            to: connection.to,
            type: connection.type,
        }));

        const updatedPosition = {
            ...position,
            planned: resetPlanned,
            connections: [],
        };
        const queue = [];

        queue.push({
            data: {
                data: {
                    guid: position.guid,
                    startDate: null,
                    endDate: null,
                },
                type: 'update',
                action: 'reset_position',
            },
            subEntityType: 'step',
            subEntityId: parentStage.id,
            t: Date.now(),
        });

        const queueConnections = [
            {
                data: {
                    data: {
                        connection: connections,
                    },
                    type: 'remove_connections',
                    action: 'reset_position',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            },
        ];

        queueFetch(queueConnections);
        queueFetch(queue);

        updateAll({
            [updatedPosition.guid]: updatedPosition,
            ...removePositionConnection(position),
        });

        dispatch(removeManyConnections(removedConnections));
    };
    const createPosition = (positionForm) => {
        const currentDate = positionForm.date
            ? positionForm.date
            : moment().hour(0).minute(0).seconds(0).format();

        const planned = {
            days_count: positionForm.days,
            start_date: currentDate,
            end_date: moment(currentDate)
                .add(+positionForm.days - 1, 'days')
                .format(),
        };

        const queue = [
            {
                data: {
                    data: {
                        guid: position.guid,
                        startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                        endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                    },
                    type: 'update',
                    action: 'create_position',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            },
        ];
        updateAll({ [position.guid]: { ...position, planned } });

        queueFetch(queue);
    };
    const increasePosition = (differenceInDays, pickerData = null) => {
        const queue = [];

        const isToConnectionsTypeOne = position.connections.some(
            (connection) => connection.type === 1 && position.guid === connection.to
        );
        const isToConnectionsTypeTwo = position.connections.some(
            (connection) => connection.type === 0 && position.guid === connection.to
        );

        const isUnchangeable = isToConnectionsTypeOne && isToConnectionsTypeTwo;

        if (isUnchangeable) {
            return;
        }

        const updateCountDays = +position.planned.days_count + differenceInDays;
        const days_count = +position.planned.days_count + differenceInDays;
        const updatedPositionsEntities = { ...positionsEntitiesAll };

        let planned = {
            ...position.planned,
            days_count: `${updateCountDays}`,
            end_date: moment(position.planned.start_date)
                .add(updateCountDays - 1, 'days')
                .format(),
        };
        if (pickerData) {
            planned = {
                days_count: pickerData.days,
                start_date: pickerData.start,
                end_date: pickerData.end,
            };
        }

        const isFollowerTypeOne = position.connections.some(
            (connection) => connection.type === 1 && connection.to === position.guid
        );

        if (isFollowerTypeOne) {
            if (!pickerData) {
                const positionEnd = position.planned.end_date;
                const days_count = +position.planned.days_count + Number(differenceInDays);
                const start_date = moment(positionEnd)
                    .subtract(days_count - 1, 'days')
                    .format();

                const planned = {
                    ...position.planned,
                    days_count,
                    start_date,
                };
                updatedPositionsEntities[position.guid] = { ...position, planned };
                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'increase_position',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });

                const backwardChain = getBackwardChain(position, positionsEntitiesAll);

                backwardChain.forEach((pred) => {
                    const updatedPred = {
                        ...pred,
                        planned: {
                            ...pred.planned,
                            start_date: moment(pred.planned.start_date)
                                .subtract(differenceInDays, 'days')
                                .format(),
                            end_date: moment(pred.planned.end_date)
                                .subtract(differenceInDays, 'days')
                                .format(),
                        },
                    };
                    updatedPositionsEntities[pred.guid] = updatedPred;
                    queue.push({
                        data: {
                            data: {
                                guid: pred.guid,
                                startDate: moment(updatedPred.planned.start_date).format(
                                    'YYYY-MM-DD'
                                ),
                                endDate: moment(updatedPred.planned.end_date).format('YYYY-MM-DD'),
                            },
                            type: 'update',
                            action: 'increase_position',
                        },
                        subEntityType: 'step',
                        subEntityId: parentStage.id,
                        t: Date.now(),
                    });
                });
                queueFetch(queue);
                updateAll(updatedPositionsEntities);
                return;
            }
            const connections = position.connections.filter(
                (connection) => connection.type === 1 && connection.to === position.guid
            );

            const firstPredecessorStartDate = connections
                .map((connection) => updatedPositionsEntities[connection.from])
                .sort(
                    (positionA, positionB) =>
                        moment(positionA.planned.end_date).valueOf() -
                        moment(positionB.planned.end_date).valueOf()
                )[0].planned.start_date;

            const updatedEndDate = moment(position.planned.start_date)
                .add(days_count - 1, 'days')
                .format();

            if (moment(updatedEndDate).isAfter(moment(firstPredecessorStartDate).add(-1, 'days'))) {
                const days_count = `${moment(firstPredecessorStartDate).diff(
                    moment(position.planned.start_date),
                    'days'
                )}`;
                const end_date = moment(firstPredecessorStartDate).add(-1, 'days').format();
                const planned = {
                    ...position.planned,
                    days_count,
                    end_date,
                };
                updatedPositionsEntities[position.guid] = { ...position, planned };

                queue.push({
                    data: {
                        data: {
                            guid: position.guid,
                            startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                            endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'increase_position',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });

                queueFetch(queue);

                updateAll(updatedPositionsEntities);
                return;
            }
        }

        const nextPositions = position.connections
            .filter((connection) => connection.type === 0 && position.guid === connection.from)
            .map((connection) => positionsEntitiesAll[connection.to]);

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getForwardChain(position, positionsEntitiesAll),
        ]);

        const moveDays = differenceInDays;

        const startDate = position.planned.start_date;
        const endDate = moment(startDate)
            .add(days_count - 1, 'days')
            .format();

        const plannedItem = {
            days_count: `${days_count}`,
            start_date: startDate,
            end_date: endDate,
        };

        const editedItem = { ...position, planned: plannedItem };

        queue.push({
            data: {
                data: {
                    guid: position.guid,
                    startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                    endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                },
                type: 'update',
                action: 'increase_position',
            },
            subEntityType: 'step',
            subEntityId: parentStage.id,
            t: Date.now(),
        });

        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = position.planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    updatePosition.forEach((position) => {
                        queue.push({
                            data: {
                                data: {
                                    guid: position.changes.guid,
                                    startDate: moment(position.changes.planned.start_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                    endDate: moment(position.changes.planned.end_date).format(
                                        'YYYY-MM-DD'
                                    ),
                                },
                                type: 'update',
                                action: 'increase_position',
                            },
                            subEntityType: 'step',
                            subEntityId: parentStage.id,
                            t: Date.now(),
                        });
                    });

                    updatedPositionsEntities[position.guid] = editedItem;

                    updatePosition.forEach((item) => {
                        updatedPositionsEntities[item.id] = item.changes;
                    });
                }
            });
        }

        updatedPositionsEntities[position.guid] = { ...position, planned };

        updateAll(updatedPositionsEntities);
        queueFetch(queue);
    };
    const decreasePosition = (differenceInDays, pickerData = null) => {
        const updateCountDays =
            +position.planned.days_count - differenceInDays < 1
                ? 1
                : +position.planned.days_count - differenceInDays;

        let planned = {
            ...position.planned,
            days_count: `${updateCountDays}`,
            end_date: moment(position.planned.start_date)
                .add(updateCountDays - 1, 'days')
                .format(),
        };
        if (pickerData) {
            planned = {
                days_count: pickerData.days,
                start_date: pickerData.start,
                end_date: pickerData.end,
            };
        }

        const queue = [
            {
                data: {
                    data: {
                        guid: position.guid,
                        startDate: moment(planned.start_date).format('YYYY-MM-DD'),
                        endDate: moment(planned.end_date).format('YYYY-MM-DD'),
                    },
                    type: 'update',
                    action: 'decrease_position',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            },
        ];

        queueFetch(queue);
        updateAll({ [position.guid]: { ...position, planned } });
    };

    const decreasePositionNew = (diff, positionGuid, positionsEntities) => {
        const result = { ...positionsEntities };

        const currentPosition = positionsEntities[positionGuid];

        const planned = {
            ...currentPosition.planned,
            days_count: `${+currentPosition.planned.days_count + diff}`,
            end_date: moment(currentPosition.planned.end_date).add(diff, 'days').format(),
        };

        result[position.guid] = { ...position, planned };

        return result;
    };

    const increasePositionNew = (differenceInDays, positionGuid, positionsEntities) => {
        const currentPosition = positionsEntities[positionGuid];
        const days_count = +currentPosition.planned.days_count + differenceInDays;
        const queue = [];
        const position = { ...currentPosition };

        const planned = {
            ...currentPosition.planned,
            days_count: `${days_count}`,
            end_date: moment(currentPosition.planned.end_date)
                .add(differenceInDays, 'days')
                .format(),
        };
        const updatedPositionsEntities = { ...positionsEntities };

        const isFollowerTypeOne = position.connections.some(
            (connection) => connection.type === 1 && connection.to === position.guid
        );

        if (isFollowerTypeOne) {
            const connections = position.connections.filter(
                (connection) => connection.type === 1 && connection.to === position.guid
            );

            const firstPredecessorStartDate = connections
                .map((connection) => updatedPositionsEntities[connection.from])
                .sort(
                    (positionA, positionB) =>
                        moment(positionA.planned.end_date).valueOf() -
                        moment(positionB.planned.end_date).valueOf()
                )[0].planned.start_date;

            const updatedEndDate = moment(position.planned.start_date)
                .add(days_count - 1, 'days')
                .format();

            if (moment(updatedEndDate).isAfter(moment(firstPredecessorStartDate).add(-1, 'days'))) {
                const days_count = `${moment(firstPredecessorStartDate).diff(
                    moment(position.planned.start_date),
                    'days'
                )}`;
                const end_date = moment(firstPredecessorStartDate).add(-1, 'days').format();
                const planned = {
                    ...position.planned,
                    days_count,
                    end_date,
                };
                updatedPositionsEntities[position.guid] = { ...position, planned };
                return updatedPositionsEntities;
            }
        }

        const nextPositions = position.connections
            .filter((connection) => connection.type === 0 && position.guid === connection.from)
            .map((connection) => positionsEntitiesAll[connection.to]);

        const chainsConnections = nextPositions.map((position) => [
            position,
            ...getChainConnection(position, positionsEntitiesAll),
        ]);

        const moveDays = differenceInDays;

        const startDate = position.planned.start_date;
        const endDate = moment(startDate)
            .add(days_count - 1, 'days')
            .format();

        const plannedItem = {
            days_count: `${days_count}`,
            start_date: startDate,
            end_date: endDate,
        };

        const editedItem = { ...position, planned: plannedItem };

        if (chainsConnections.length) {
            chainsConnections.forEach((chain) => {
                const nextPosition = chain[0];

                const initEndDate = position.planned.end_date;

                const nextPositionStartDate = nextPosition.planned.start_date;

                const diffBeforePosition =
                    moment(nextPositionStartDate).diff(moment(initEndDate), 'days') - 1;

                if (diffBeforePosition - moveDays < 0) {
                    const diff = Math.abs(diffBeforePosition - moveDays);

                    const updatePosition = getUpdatePositions(chain, diff);

                    updatedPositionsEntities[position.guid] = editedItem;

                    updatePosition.forEach((item) => {
                        updatedPositionsEntities[item.id] = item.changes;
                    });
                }
            });
        }

        updatedPositionsEntities[position.guid] = { ...position, planned };

        return updatedPositionsEntities;
    };
    const movePosition = (moveDays, positionsEntities) => {
        let updatedPositionsEntities = { ...positionsEntities };

        const start_date = moment(position.planned.start_date).add(moveDays, 'days').format();
        const end_date = moment(position.planned.end_date).add(moveDays, 'days').format();

        let updatedPlanned = {
            ...position.planned,
            start_date,
            end_date,
        };

        const connectionsTypeOne = position.connections.filter(
            (connection) => connection.type === 1
        );

        const nextPositions = position.connections
            .filter((connection) => connection.type === 0 && position.guid === connection.from)
            .map((connection) => positionsEntitiesAll[connection.to])
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );

        const prevPositions = position.connections
            .filter((connection) => connection.type === 0 && connection.from !== position.guid)
            .map((connection) => positionsEntitiesAll[connection.from])
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );
        const isFollowerTypeOne = position.connections.some(
            (connection) => connection.type === 1 && connection.to === position.guid
        );

        const updatedEndDate = moment(position.planned.end_date).add(moveDays, 'days').format();

        if (isFollowerTypeOne) {
            return updatePositionIfFollowerTypeOne(
                moveDays,
                updatedEndDate,
                updatedPositionsEntities
            );
        }

        if (connectionsTypeOne.length) {
            const resultUpdatedFollowersOne = updatedFollowersTypeOne(
                moveDays,
                connectionsTypeOne,
                updatedPositionsEntities
            );
            updatedPositionsEntities = {
                ...updatedPositionsEntities,
                ...resultUpdatedFollowersOne,
            };
        }

        if (prevPositions.length) {
            updatedPlanned = updatePositionWithPrev(prevPositions, updatedPlanned);
        }

        if (nextPositions.length) {
            const updatedPosition = { ...position, planned: updatedPlanned };
            const resultUpdateNextPosition = updateNextPosition(
                nextPositions,
                updatedPosition,
                moveDays,
                updatedPositionsEntities
            );
            updatedPositionsEntities = {
                ...updatedPositionsEntities,
                ...resultUpdateNextPosition,
            };
        }

        updatedPositionsEntities[position.guid] = { ...position, planned: updatedPlanned };

        return updatedPositionsEntities;
    };
    const onDataPickerPosition = (position, pickerData) => {
        const positionInitStartDate = position.planned.start_date;

        const isNeedMove = pickerData.start !== position.planned.start_date;
        const isNeedResize = pickerData.days !== position.planned.days_count;
        const moveDays = moment(pickerData.start).diff(moment(positionInitStartDate), 'days');

        let resultUpdating = {};
        resultUpdating[position.guid] = { ...position };

        if (isNeedMove) {
            const moveResult = movePosition(moveDays, resultUpdating);
            resultUpdating = { ...resultUpdating, ...moveResult };
        }

        if (isNeedResize) {
            const isDecrease = +position.planned.days_count > +pickerData.days;
            const diff = +pickerData.days - +position.planned.days_count;

            if (isDecrease) {
                const decreaseResult = decreasePositionNew(diff, position.guid, resultUpdating);
                resultUpdating = { ...resultUpdating, ...decreaseResult };
            } else {
                const increaseResult = increasePositionNew(diff, position.guid, resultUpdating);
                resultUpdating = { ...resultUpdating, ...increaseResult };
            }
        }

        const queue = [];
        Object.values(resultUpdating).forEach((position) => {
            queue.push({
                data: {
                    data: {
                        guid: position.guid,
                        startDate: moment(position.planned.start_date).format('YYYY-MM-DD'),
                        endDate: moment(position.planned.end_date).format('YYYY-MM-DD'),
                    },
                    type: 'update',
                    action: 'picker_update_position',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            });
        });
        queueFetch(queue);
        updateAll(resultUpdating);
    };

    return {
        resetPosition,
        createPosition,
        decreasePosition,
        increasePosition,
        onDataPickerPosition,
    };
};
